/**
 * PersonalDetailsSection — the full block of "stuff every form asks for".
 *
 * Six collapsible groups, mirroring the chronology of a UK job application:
 *   1. Basic Identity       (title, names, contact, address, postcode)
 *   2. Work Eligibility     (RTW, visa, sponsorship, nationality)
 *   3. Professional         (current role, salary, notice, availability, working pattern)
 *   4. Driving & Travel     (licence, vehicle, travel, weekends/evenings)
 *   5. Security & Compliance (convictions, clearance, background/financial checks)
 *   6. Online Presence      (LinkedIn, GitHub, website, portfolio)
 *
 * Same UX pattern as EqualOppsSection — debounced auto-save, "lock it in" button,
 * collapse-all toggle. Talks to GET/PUT /api/profile (personal_info shape).
 * Merges on save so other components writing to the same blob (UKEssentials,
 * Profile page, EqualOpps mirroring) never wipe each other.
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  IdentificationCard, Globe, Briefcase, Car, ShieldCheck, LinkSimple,
  CaretDown, CaretUp, LockKey, User,
} from "@phosphor-icons/react";

// ---------- helpers ----------
function useDebounced(fn, delay = 800) {
  const ref = useRef(fn);
  const timer = useRef(null);
  useEffect(() => { ref.current = fn; }, [fn]);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  const debounced = (...args) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => ref.current(...args), delay);
  };
  debounced.cancel = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };
  return debounced;
}

// ---------- choice lists ----------
const PNTS = "Prefer not to say";

const TITLE = ["", "Mr", "Mrs", "Ms", "Mx", "Miss", "Dr", "Prof", "Other", PNTS];

const SPONSORSHIP = ["", "No — I don't need sponsorship", "Yes — I need sponsorship now", "Yes — I'll need it in future", PNTS];

const VISA_TYPE = [
  "",
  "Not applicable (British / Irish citizen)",
  "Settled status (EU Settlement Scheme)",
  "Pre-Settled status (EU Settlement Scheme)",
  "Skilled Worker visa",
  "Graduate visa",
  "Student visa",
  "High Potential Individual visa",
  "Global Talent visa",
  "Spouse / Partner visa",
  "Ancestry visa",
  "Youth Mobility Scheme",
  "Indefinite Leave to Remain",
  "Refugee / Asylum status",
  "Other",
];

const WORKING_PATTERN = ["", "Full-time", "Part-time", "Flexible / open", "Contract", "Freelance / self-employed", "Internship / placement"];

const NOTICE_PERIOD = ["", "Immediately available", "1 week", "2 weeks", "1 month", "2 months", "3 months", "Longer than 3 months"];

const WILLING_RELOCATE = ["", "Yes", "No", "Maybe — for the right role", "Already willing to commute"];

const DRIVING_LICENCE = ["", "None", "Provisional", "Full UK", "Full international", "Full UK + own vehicle"];

const VEHICLE_ACCESS = ["", "Yes", "No"];

const WILLING_TRAVEL = ["", "None", "Occasional (a few times/year)", "Regular (monthly)", "Frequent (weekly)", "Anywhere / no limit"];

const WEEKENDS_EVENINGS = ["", "Yes — happy to", "No", "If required occasionally", "Already part of my role"];

const CRIMINAL_CONVICTIONS = [
  "",
  "None",
  "Spent — declarable under ROA only if asked",
  "Unspent — will declare",
  PNTS,
];

const SECURITY_CLEARANCE = ["", "None", "BPSS", "CTC (Counter Terrorist Check)", "SC (Security Check)", "DV (Developed Vetting)", "Other"];

const yes_no_pnts = ["", "Yes", "No", PNTS];

// ---------- groups definition ----------
const GROUPS = [
  {
    key: "identity",
    label: "Basic identity",
    icon: IdentificationCard,
    stripe: "var(--brand-primary)",
    hint: "the top of any application form.",
    fields: [
      ["title", "Title", "select", TITLE],
      ["full_name", "Full legal name", "input", null, "as it appears on your passport / ID"],
      ["preferred_name", "Preferred name (optional)", "input", null, "what you go by day-to-day"],
      ["pronouns", "Pronouns (optional)", "select", ["", "she/her", "he/him", "they/them", "she/they", "he/they", "prefer to self-describe", "Prefer not to say"], "increasingly asked on UK applications"],
      ["email", "Email", "input"],
      ["phone", "Phone", "input", null, "include country code if international"],
      ["home_address", "Home address", "textarea", null, "line 1 + 2 + town/city"],
      ["postcode", "Postcode", "input"],
      ["country", "Country", "input", null, "country of residence — e.g. United Kingdom"],
      ["location", "Short location label", "input", null, "e.g. 'London, UK' — used on share links"],
    ],
  },
  {
    key: "eligibility",
    label: "Work eligibility",
    icon: Globe,
    stripe: "var(--bolt-sage)",
    hint: "right-to-work, visa, sponsorship — entered once, never typed again.",
    fields: [
      ["nationality", "Nationality", "input", null, "for eligibility, not monitoring"],
      ["right_to_work_status", "Right-to-work status", "input", null, "e.g. British citizen, Settled status, Skilled Worker visa"],
      ["visa_type", "Visa type (if applicable)", "select", VISA_TYPE],
      ["visa_expiry", "Visa expiry date (if applicable)", "date"],
      ["sponsorship_needed", "Do you need sponsorship?", "select", SPONSORSHIP],
      ["right_to_work_notes", "Visa / permit notes (optional)", "textarea", null, "anything an employer would need to know"],
    ],
  },
  {
    key: "professional",
    label: "Current role & availability",
    icon: Briefcase,
    stripe: "var(--brand-primary)",
    hint: "where you are now + what you're looking for next.",
    fields: [
      ["current_employer", "Current employer", "input"],
      ["current_title", "Current job title", "input"],
      ["current_salary", "Current salary (optional)", "input", null, "e.g. £38,000 or USD 75k"],
      ["salary_expectations", "Salary expectations", "input", null, "range is fine — e.g. £45–55k"],
      ["notice_period", "Notice period", "select", NOTICE_PERIOD],
      ["earliest_start_date", "Earliest start date", "date"],
      ["working_pattern", "Preferred working pattern", "select", WORKING_PATTERN],
      ["willing_to_relocate", "Willing to relocate?", "select", WILLING_RELOCATE],
    ],
  },
  {
    key: "driving",
    label: "Driving & travel",
    icon: Car,
    stripe: "var(--bolt-sage)",
    hint: "only matters for role-dependent jobs (sales, healthcare, hospitality, etc.).",
    fields: [
      ["driving_licence", "Driving licence", "select", DRIVING_LICENCE],
      ["vehicle_access", "Access to a vehicle", "select", VEHICLE_ACCESS],
      ["willing_to_travel", "Willingness to travel", "select", WILLING_TRAVEL],
      ["weekends_evenings", "Available weekends / evenings", "select", WEEKENDS_EVENINGS],
    ],
  },
  {
    key: "security",
    label: "Security & compliance",
    icon: ShieldCheck,
    stripe: "var(--brand-primary)",
    hint: "for finance, public-sector, security-cleared, regulated industries.",
    fields: [
      ["criminal_convictions", "Criminal convictions (ROA-compliant)", "select", CRIMINAL_CONVICTIONS],
      ["conviction_details", "Details (only if asked, and only declarable items)", "textarea", null, "you don't have to declare spent convictions for most roles — read about the Rehabilitation of Offenders Act"],
      ["security_clearance", "Highest security clearance held", "select", SECURITY_CLEARANCE],
      ["background_check_ok", "Eligible for background checks", "select", yes_no_pnts],
      ["financial_check_ok", "Eligible for financial checks (FS roles)", "select", yes_no_pnts],
    ],
  },
  {
    key: "regulated",
    label: "Regulated profession",
    icon: ShieldCheck,
    stripe: "var(--bolt-sage)",
    hint: "solicitors, barristers, accountants, medics — only relevant if your profession requires registration.",
    fields: [
      ["sra_number", "SRA number (solicitors)", "input", null, "6-digit Solicitors Regulation Authority ID"],
      ["solicitor_qualification_date", "Date of qualification as solicitor", "date", null, "date of admission"],
      ["bar_number", "Bar number (barristers)", "input"],
      ["practising_certificate", "Practising certificate number", "input", null, "PC number if applicable"],
    ],
  },
  {
    key: "adjustments",
    label: "Reasonable adjustments",
    icon: Globe,
    stripe: "var(--brand-primary)",
    hint: "stored ONLY if you choose to — filled into forms that ask, ignored otherwise.",
    fields: [
      ["has_disability", "Do you consider yourself to have a disability?", "select", ["", "yes", "no", "prefer_not_to_say"], "Equality Act 2010 definition"],
      ["requires_adjustments", "Do you require any reasonable adjustments for interview?", "select", ["", "yes", "no"]],
      ["adjustments_description", "Adjustments needed (if yes)", "textarea", null, "e.g. step-free access, extra time, screen-reader-compatible tests"],
    ],
  },
  {
    key: "online",
    label: "Online presence",
    icon: LinkSimple,
    stripe: "var(--bolt-sage)",
    hint: "the links you paste into every application.",
    fields: [
      ["linkedin", "LinkedIn URL", "input", null, "linkedin.com/in/yourname"],
      ["github_url", "GitHub URL", "input", null, "github.com/yourhandle"],
      ["website", "Personal website", "input"],
      ["portfolio", "Portfolio link", "input", null, "Behance, Dribbble, Notion, etc."],
      ["headline", "One-line headline", "input", null, "e.g. 'Final-year CS student. Building scrappy SaaS.'"],
      ["summary", "Personal pitch / about me", "textarea", null, "2–3 sentences. What you do, what you want."],
    ],
  },
];

// ---------- field renderer ----------
function Field({ k, label, type, options, hint, value, onChange }) {
  return (
    <label className="block" data-testid={`pd-field-${k}`}>
      <span className="label-tiny block mb-1">{label}</span>
      {type === "select" ? (
        <select value={value || ""}
          data-testid={`pd-input-${k}`}
          onChange={(e) => onChange(e.target.value)}
          className="w-full card-hard rounded-md px-3 py-2 bg-white text-sm">
          {options.map((o) => <option key={o} value={o}>{o || "— select —"}</option>)}
        </select>
      ) : type === "textarea" ? (
        <textarea value={value || ""}
          data-testid={`pd-input-${k}`}
          onChange={(e) => onChange(e.target.value)}
          placeholder={hint || ""}
          className="w-full card-hard rounded-md px-3 py-2 bg-white text-sm min-h-[72px]"/>
      ) : type === "date" ? (
        <input type="date" value={value || ""}
          data-testid={`pd-input-${k}`}
          onChange={(e) => onChange(e.target.value)}
          className="w-full card-hard rounded-md px-3 py-2 bg-white text-sm"/>
      ) : (
        <input value={value || ""}
          data-testid={`pd-input-${k}`}
          onChange={(e) => onChange(e.target.value)}
          placeholder={hint || ""}
          className="w-full card-hard rounded-md px-3 py-2 bg-white text-sm"/>
      )}
      {hint && type !== "input" && type !== "textarea" && (
        <p className="font-mono-d text-[10px] text-[var(--brand-muted)] mt-1">{hint}</p>
      )}
    </label>
  );
}

// ---------- group card ----------
function GroupCard({ group, form, onChange, collapsed, onToggleCollapse, filled }) {
  const Icon = group.icon;
  return (
    <div className="card-hard rounded-md bg-white overflow-hidden relative" data-testid={`pd-group-${group.key}`}>
      <span aria-hidden="true" className="absolute left-0 top-0 bottom-0 w-1.5" style={{ background: group.stripe }}/>
      <button onClick={onToggleCollapse}
        data-testid={`pd-group-toggle-${group.key}`}
        className="w-full flex items-center justify-between gap-3 p-3 pl-5 text-left hover:bg-[var(--brand-bg)] transition">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Icon size={16} weight="duotone" style={{ color: group.stripe }}/>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm">{group.label}</p>
            {group.hint && <p className="text-[11px] text-[var(--brand-muted)] mt-0.5 truncate">{group.hint}</p>}
          </div>
        </div>
        <span className="font-mono-d text-[10px] text-[var(--brand-muted)] shrink-0">
          {filled}/{group.fields.length}
        </span>
        {collapsed ? <CaretDown size={16} weight="bold"/> : <CaretUp size={16} weight="bold"/>}
      </button>
      {!collapsed && (
        <div className="px-5 pb-5 pt-1 grid grid-cols-1 md:grid-cols-2 gap-3">
          {group.fields.map(([k, label, type, options, hint]) => (
            <Field key={k} k={k} label={label} type={type} options={options} hint={hint}
              value={form[k]}
              onChange={(v) => onChange(k, v)}/>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- main section ----------
export default function PersonalDetailsSection() {
  const [form, setForm] = useState(null);
  // start with everything collapsed except the first group
  // Land with EVERY group collapsed for neatness — user can expand what they want.
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set(GROUPS.map((g) => g.key)));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  useEffect(() => {
    api.get("/profile").then(({ data }) => setForm(data?.personal_info || {})).catch(() => setForm({}));
  }, []);

  const persist = useDebounced(async (next) => {
    setSaving(true);
    try {
      // merge with the existing personal_info blob so we never wipe fields owned by
      // other components (e.g. NI number, equal-opps mirrors).
      const { data } = await api.get("/profile");
      const merged = { ...(data?.personal_info || {}), ...next };
      await api.put("/profile", merged);
      setDirty(false);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2500);
    } catch { toast.error("Couldn't save"); }
    finally { setSaving(false); }
  }, 800);

  const onChange = (key, value) => {
    const next = { ...form, [key]: value };
    setForm(next);
    setDirty(true);
    persist(next);
  };

  const toggleGroup = (key) => {
    setCollapsedGroups((cur) => {
      const next = new Set(cur);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const expandAll = () => setCollapsedGroups(new Set());
  const collapseAll = () => setCollapsedGroups(new Set(GROUPS.map((g) => g.key)));

  const lockIn = async () => {
    persist.cancel?.();
    setSaving(true);
    try {
      const { data } = await api.get("/profile");
      const merged = { ...(data?.personal_info || {}), ...form };
      await api.put("/profile", merged);
      setDirty(false);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2500);
      toast.success("Locked into the vault");
      collapseAll();
    } catch { toast.error("Couldn't lock it in — try again"); }
    finally { setSaving(false); }
  };

  if (form === null) return null;

  const totalFilled = Object.values(form).filter((v) => v && v.toString().trim() !== "").length;
  // 'any collapsed' triggers the 'expand all' label — so a single click from any state goes to all-open.
  const anyCollapsed = collapsedGroups.size > 0;
  const filledPerGroup = (group) => group.fields.filter(([k]) => {
    const v = form[k];
    return v != null && v.toString().trim() !== "";
  }).length;

  return (
    <section className="space-y-4" data-testid="section-personal-details">
      <div className="card-hard rounded-md bg-white p-5 relative overflow-hidden">
        <span aria-hidden="true" className="absolute left-0 top-0 bottom-0 w-1.5 bg-[var(--brand-primary)]"/>
        <div className="flex items-start justify-between gap-3 pl-2">
          <div>
            <p className="label-tiny flex items-center gap-1.5 m-0">
              <User size={12} weight="duotone" className="text-[var(--brand-primary)]"/> /personal details
            </p>
            <h2 className="font-display text-2xl tracking-tight mt-1">Everything every form asks.</h2>
            <p className="text-xs text-[var(--brand-muted)] mt-1 max-w-2xl">
              the entire &ldquo;tell us about yourself&rdquo; block of a UK job application — identity, eligibility,
              current role, driving, security, online links. enter once, never type again. saves automatically.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2 shrink-0">
            <span className="font-mono-d text-xs text-[var(--brand-muted)]">
              {saving ? "saving…" : dirty ? "unsaved" : justSaved ? <span className="text-[var(--bolt-sage)]">saved</span> : `${totalFilled} answered`}
            </span>
            <button onClick={anyCollapsed ? expandAll : collapseAll}
              data-testid="pd-collapse-all"
              className="btn-hard bg-white rounded-md px-3 py-1.5 text-xs flex items-center gap-1">
              {anyCollapsed ? <CaretDown size={12} weight="bold"/> : <CaretUp size={12} weight="bold"/>}
              {anyCollapsed ? "expand all" : "collapse all"}
            </button>
          </div>
        </div>
      </div>

      {GROUPS.map((g) => (
        <GroupCard key={g.key} group={g} form={form} onChange={onChange}
          collapsed={collapsedGroups.has(g.key)}
          onToggleCollapse={() => toggleGroup(g.key)}
          filled={filledPerGroup(g)}/>
      ))}

      <div className="card-hard rounded-md bg-white p-5 flex items-center justify-between gap-3 flex-wrap">
        <p className="font-mono-d text-[11px] text-[var(--brand-muted)] max-w-md">
          auto-saves as you type · hit <b>lock it in</b> when you&apos;re done — it confirms the whole block and collapses everything.
        </p>
        <button onClick={lockIn} disabled={saving}
          data-testid="pd-lock"
          className="btn-hard bg-[var(--brand-ink)] text-white rounded-md px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-60">
          <LockKey size={14} weight="bold"/> {saving ? "locking…" : "lock it in"}
        </button>
      </div>
    </section>
  );
}
